import { useCallback } from 'react';

import Clickable from '@perpay-web/components/base/Clickable/Clickable';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { useAnimatedModal } from '@perpay-web/components/base/Modal/useAnimatedModal';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import NonFieldErrors from '@perpay-web/components/base/NonFieldErrors/NonFieldErrors';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Inline from '@perpay-web/components/design-system/Inline/Inline';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import DirectDepositInfo from '@perpay-web/fintech/components/base/DirectDepositInfo/DirectDepositInfo';
import CardApplicationHeader from '@perpay-web/fintech/components/base/Header/CardApplicationHeader';
import Stepper from '@perpay-web/fintech/components/base/Stepper/Stepper';
import CardDirectDepositSummaryMetal from '@perpay-web/fintech/components/composite/CardDirectDepositSummaryMetal/CardDirectDepositSummaryMetal';
import DirectDepositDrawerMetal from '@perpay-web/fintech/components/composite/DirectDepositDrawerMetal/DirectDepositDrawerMetal';
import {
    cardApplicationStepperNames,
    REVIEW_DIRECT_DEPOSIT_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { getClassName } from '@perpay-web/utils/domUtils';
import {
    formatCurrency,
    formatCurrencyConditionalCents,
} from '@perpay-web/utils/stringUtils';

import styles from './CardApplicationDirectDepositSummary.scss';

const DIRECT_DEPOSIT_DISCLAIMER = (
    <>
        Direct deposit is required in order to access the Perpay Credit Card.
        *You will have control of funds that are sent to Perpay.{' '}
        <span className={styles['disclaimer--bold']}>
            Your offer will expire if your direct deposit is not received within
            60 days.
        </span>
    </>
);

const CardApplicationDirectDepositSummary = ({
    isLoading,
    onMount,
    onUnmount,
    creditLimit,
    isMPUser,
    totalMinimumPayment,
    coreMinimumPayment,
    cardMinimumPayment,
    payCycleType,
    onClickContinue,
    errors,
    onClickLearnMore,
    onDirectDepositModalViewed,
    onDirectDepositModalClosed,
    partnerCode,
    welcomeOffer,
}) => {
    const { isModalOpen, setIsModalOpen } = useModal();
    const {
        isAnimatedModalOpen,
        setIsAnimatedModalOpen,
        animatedModalClassName,
    } = useAnimatedModal({ isModalOpen, setIsModalOpen });

    const creditLimitString = formatCurrency(creditLimit, true, false);

    useMountAndUnmount(() => {
        onMount();
        return onUnmount;
    });

    const renderStepper = () => {
        const currentStep = cardApplicationStepperNames.findIndex(
            (step) => step.step === REVIEW_DIRECT_DEPOSIT_STEP,
        );

        return (
            <Stepper
                steps={cardApplicationStepperNames}
                currentStep={currentStep}
                className={getClassName(
                    styles.stepper,
                    welcomeOffer ? styles.stepper__offer : '',
                )}
            />
        );
    };

    const renderWelcomeOffer = () =>
        welcomeOffer ? (
            <div className={styles['welcome-offer']}>
                ${welcomeOffer} Welcome Bonus
            </div>
        ) : null;

    const renderFullHeaderCb = useCallback(
        () => <CardApplicationHeader partnerCode={partnerCode} />,
        [partnerCode],
    );

    useFullHeader(renderFullHeaderCb);

    if (isLoading) {
        return <WarningLoaderCircle />;
    }

    const cardMinimumPaymentFormatted =
        formatCurrencyConditionalCents(cardMinimumPayment);

    const coreMinimumPaymentFormatted =
        formatCurrencyConditionalCents(coreMinimumPayment);

    const totalMinimumPaymentFormatted =
        formatCurrencyConditionalCents(totalMinimumPayment);

    const isCardMinimumPayment = cardMinimumPayment > 0;

    return (
        <FocusLayout className={styles.container}>
            {renderWelcomeOffer()}
            {renderStepper()}
            <FocusContent className={styles.content}>
                <Stack className={styles['inner-stack-tight']}>
                    <div className={styles.header}>
                        Direct deposit & your card
                    </div>
                    <div className={styles.copy}>
                        With the Perpay Credit Card, your paycheck is your
                        superpower!
                    </div>
                </Stack>
                <DirectDepositInfo />
                <Stack className={styles.details}>
                    <div className={styles.details__title}>
                        Direct Deposit Details
                    </div>
                    <Inline className={styles.details__items}>
                        <div className={styles.details__item}>
                            <div className={styles['details__item-title']}>
                                Starting Credit Limit
                            </div>
                            <div className={styles['details__item-amount']}>
                                {creditLimitString}
                            </div>
                        </div>

                        <div className={styles.details__item}>
                            <div className={styles['details__item-title']}>
                                Min card direct deposit
                            </div>
                            <div className={styles['details__item-amount']}>
                                {cardMinimumPaymentFormatted}
                                /pay
                            </div>
                        </div>
                    </Inline>
                </Stack>
                {isMPUser ? (
                    <CardDirectDepositSummaryMetal
                        className={styles.summary}
                        totalAmount={totalMinimumPaymentFormatted}
                        coreAmount={coreMinimumPaymentFormatted}
                        cardAmount={cardMinimumPaymentFormatted}
                        isCardMinimumPayment={isCardMinimumPayment}
                        payCycleType={payCycleType}
                        headingText='Total Required Direct Deposit'
                        showSubHeader={false}
                    />
                ) : null}
                <div className={styles.disclaimer__mobile}>
                    {DIRECT_DEPOSIT_DISCLAIMER}
                </div>

                {errors ? <NonFieldErrors errors={errors} /> : null}
                {isAnimatedModalOpen ? (
                    <DirectDepositDrawerMetal
                        className={animatedModalClassName}
                        onClose={() => {
                            setIsAnimatedModalOpen(false);
                            onDirectDepositModalClosed();
                        }}
                        onMount={onDirectDepositModalViewed}
                    />
                ) : null}
            </FocusContent>

            <FocusFooter className={styles['footer-container']}>
                <div className={styles.footer__disclaimer}>
                    {DIRECT_DEPOSIT_DISCLAIMER}
                </div>
                <CardCta
                    className={styles.footer__button}
                    onClick={onClickContinue}
                    Component={CustomButton}
                >
                    Continue
                </CardCta>
                <Clickable
                    className={styles.footer__learn}
                    onClick={() => {
                        onClickLearnMore();
                        setIsAnimatedModalOpen(true);
                    }}
                >
                    <div className={styles['footer__learn-text']}>
                        How direct deposit works at Perpay
                    </div>
                </Clickable>
            </FocusFooter>
        </FocusLayout>
    );
};

export default CardApplicationDirectDepositSummary;
